/**
 * useACCConnection — ACC (Autodesk Construction Cloud) connection subsystem for SitesPage.
 *
 * Extracted verbatim from src/components/SitesPage.tsx (state cluster + handlers).
 * Behavior-preserving: initial values, control flow, and console/alert output are
 * byte-identical to the originals. Component-scope values the handler bodies read
 * (selectedAsset, loadAssets, and the two parent callbacks) arrive as params.
 *
 * NOTE (partial-by-design): refreshingRvtLevels + refreshAccRvtLevels stay in SitesPage —
 * that state/helper is shared with the local-RVT refresh flow, so it is not pure ACC.
 */
import { useState } from 'react';
import { Asset } from '../../services/AssetService';
import {
  accService,
  ACCConnection,
  ACCHub,
  ACCProject,
  ACCFolderItem,
  ACCLink,
  ACCFileVersion,
} from '../../services/ACCService';
import { showErrorToast } from '../../components/shared/toast';
import { logger } from '../../utils/log';

interface UseACCConnectionParams {
  selectedAsset: Asset | null;
  loadAssets: () => void | Promise<void>;
  onAccLinksChanged?: () => void;
  onOpenAutodeskViewer?: (config: {
    urn: string;
    connectionId: string;
    assetId?: string;
    viewables: Array<{ guid: string; name: string; role: '3d' | '2d' }>;
  }) => void;
}

export function useACCConnection({
  selectedAsset,
  loadAssets,
  onAccLinksChanged,
  onOpenAutodeskViewer,
}: UseACCConnectionParams) {
  const [showACCBrowserModal, setShowACCBrowserModal] = useState(false);
  const [accConnections, setAccConnections] = useState<ACCConnection[]>([]);
  const [accSelectedConnection, setAccSelectedConnection] = useState<ACCConnection | null>(null);
  const [accHubs, setAccHubs] = useState<ACCHub[]>([]);
  const [accSelectedHub, setAccSelectedHub] = useState<ACCHub | null>(null);
  const [accProjects, setAccProjects] = useState<ACCProject[]>([]);
  const [accSelectedProject, setAccSelectedProject] = useState<ACCProject | null>(null);
  const [accFolderContents, setAccFolderContents] = useState<ACCFolderItem[]>([]);
  const [accFolderPath, setAccFolderPath] = useState<{id: string; name: string}[]>([]);
  const [accLoading, setAccLoading] = useState(false);
  const [accLinks, setAccLinks] = useState<ACCLink[]>([]);
  const [accLinking, setAccLinking] = useState(false);
  const [accSelectedFileForLink, setAccSelectedFileForLink] = useState<ACCFolderItem | null>(null);
  const [accConversionType, setAccConversionType] = useState<'ifc' | 'rvt'>('ifc');
  const [accDisconnecting, setAccDisconnecting] = useState(false);
  const [assetsWithAccLinks, setAssetsWithAccLinks] = useState<Set<string>>(new Set());
  const [showAccScopeModal, setShowAccScopeModal] = useState(false);
  const [accSelectedScope, setAccSelectedScope] = useState<'readonly' | 'full'>('full');
  const [showAccVersionModal, setShowAccVersionModal] = useState(false);
  const [accFileVersions, setAccFileVersions] = useState<ACCFileVersion[]>([]);
  const [accSelectedVersion, setAccSelectedVersion] = useState<number | null>(null); // null = "Always Latest"
  const [accSelectedVersionUrn, setAccSelectedVersionUrn] = useState<string | null>(null);
  const [accVersionsLoading, setAccVersionsLoading] = useState(false);
  const [accEnableMasterViews, setAccEnableMasterViews] = useState(true);
  const [accPendingLinkFile, setAccPendingLinkFile] = useState<ACCFolderItem | null>(null); // File waiting for version selection
  const [accChangeVersionLink, setAccChangeVersionLink] = useState<ACCLink | null>(null); // Existing link being re-versioned
  const [accViewDirectMode, setAccViewDirectMode] = useState(false); // True when using "View Direct" instead of "Link"

  // ACC Helper Functions
  const handleConnectACC = async () => {
    if (!selectedAsset) {
      showErrorToast('Please select a site first');
      return;
    }

    try {
      setAccLoading(true);
      // Load existing connections for this site
      const connections = await accService.getConnections(selectedAsset.id);
      setAccConnections(connections);

      if (connections.length > 0) {
        // User has connections, show browser modal
        setShowACCBrowserModal(true);
      } else {
        // No connections, show scope selection modal first
        setShowAccScopeModal(true);
      }
    } catch (error) {
      console.error('Failed to connect ACC:', error);
      showErrorToast('Failed to connect to Autodesk Construction Cloud');
    } finally {
      setAccLoading(false);
    }
  };

  // Handle scope selection confirmation and redirect to Autodesk OAuth
  const handleAccScopeConfirm = async () => {
    if (!selectedAsset) {
      showErrorToast('Please select a site first');
      setShowAccScopeModal(false);
      return;
    }

    try {
      setAccLoading(true);
      setShowAccScopeModal(false);
      // Pass assetId - connections are per-site
      const { authUrl } = await accService.initiateAuth(selectedAsset.id, '/sites', accSelectedScope);
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to initiate OAuth:', error);
      showErrorToast('Failed to connect to Autodesk');
      setAccLoading(false);
    }
  };

  const handleDisconnectACC = async (connection: ACCConnection) => {
    // `accLinks` holds only the CURRENTLY SELECTED site's links, so this count is a floor, not
    // a total — the same connection may serve models on sites not loaded here. The wording says
    // "at least" rather than quoting a number that looks authoritative and is not.
    //
    // Before migration 066 this dialog was also simply wrong: it promised the files "will no
    // longer sync automatically" while the FK actually DELETED every link row, taking each
    // model's viewable, level and room-source choices with it, across every site. Now the rows
    // survive with no credential, which is what this text always claimed.
    const linkedHere = accLinks.filter(link => link.acc_connection_id === connection.id).length;
    const warningMessage = linkedHere > 0
      ? `Disconnect "${connection.acc_account_name}"?\n\n`
        + `At least ${linkedHere} linked model(s) use this account — possibly more on other sites.\n\n`
        + 'They are kept, along with their level and viewable settings, but will stop loading '
        + 'and syncing until the account is reconnected.'
      : `Disconnect "${connection.acc_account_name}"?`;

    if (!confirm(warningMessage)) return;

    try {
      setAccDisconnecting(true);
      await accService.deleteConnection(connection.id);
      // Refresh connections list for this site
      if (selectedAsset) {
        const connections = await accService.getConnections(selectedAsset.id);
        setAccConnections(connections);
        // Refresh ACC links
        loadACCLinks(selectedAsset.id);
      }
    } catch (error) {
      console.error('Failed to disconnect ACC:', error);
      showErrorToast('Failed to disconnect Autodesk account');
    } finally {
      setAccDisconnecting(false);
    }
  };

  const handleACCSelectConnection = async (connection: ACCConnection) => {
    setAccSelectedConnection(connection);
    setAccSelectedHub(null);
    setAccSelectedProject(null);
    setAccFolderContents([]);
    setAccFolderPath([]);

    try {
      setAccLoading(true);
      const hubs = await accService.getHubs(connection.id);
      setAccHubs(hubs);
    } catch (error) {
      console.error('Failed to load hubs:', error);
      showErrorToast('Failed to load Autodesk accounts');
    } finally {
      setAccLoading(false);
    }
  };

  const handleACCSelectHub = async (hub: ACCHub) => {
    setAccSelectedHub(hub);
    setAccSelectedProject(null);
    setAccFolderContents([]);
    setAccFolderPath([]);

    try {
      setAccLoading(true);
      const projects = await accService.getProjects(accSelectedConnection!.id, hub.id);
      setAccProjects(projects);
    } catch (error) {
      console.error('Failed to load projects:', error);
      showErrorToast('Failed to load projects');
    } finally {
      setAccLoading(false);
    }
  };

  const handleACCSelectProject = async (project: ACCProject) => {
    setAccSelectedProject(project);
    setAccFolderPath([]);

    try {
      setAccLoading(true);
      // Pass hubId for top-level folders
      const contents = await accService.getFolderContents(
        accSelectedConnection!.id,
        project.id,
        undefined,
        accSelectedHub?.id
      );
      setAccFolderContents(contents);
    } catch (error) {
      console.error('Failed to load folder contents:', error);
      showErrorToast('Failed to load folder contents');
    } finally {
      setAccLoading(false);
    }
  };

  const handleACCNavigateFolder = async (folder: ACCFolderItem) => {
    if (folder.type !== 'folder') return;

    setAccFolderPath([...accFolderPath, { id: folder.id, name: folder.name }]);

    try {
      setAccLoading(true);
      // When navigating into a subfolder, folderId is provided so hubId not needed
      const contents = await accService.getFolderContents(
        accSelectedConnection!.id,
        accSelectedProject!.id,
        folder.id
      );
      setAccFolderContents(contents);
    } catch (error) {
      console.error('Failed to load folder contents:', error);
      showErrorToast('Failed to load folder contents');
    } finally {
      setAccLoading(false);
    }
  };

  const handleACCNavigateUp = async () => {
    if (accFolderPath.length === 0) return;

    const newPath = accFolderPath.slice(0, -1);
    setAccFolderPath(newPath);

    try {
      setAccLoading(true);
      const folderId = newPath.length > 0 ? newPath[newPath.length - 1].id : undefined;
      // Pass hubId when navigating back to root (folderId undefined)
      const contents = await accService.getFolderContents(
        accSelectedConnection!.id,
        accSelectedProject!.id,
        folderId,
        folderId ? undefined : accSelectedHub?.id
      );
      setAccFolderContents(contents);
    } catch (error) {
      console.error('Failed to load folder contents:', error);
    } finally {
      setAccLoading(false);
    }
  };

  const handleACCLinkFile = async (file: ACCFolderItem, conversionType: 'ifc' | 'svf2') => {
    // Use file.id (item ID) for getting tip version, not file.urn
    if (!selectedAsset || !accSelectedConnection || !accSelectedProject || !file.id) return;

    // Info about read-only connections (uses existing ACC translation)
    if (accSelectedConnection.scope_level === 'readonly') {
      const proceed = confirm('This ACC connection has View Only permissions.\n\n3D viewing will use ACC\'s existing translation (if available).\nNote: Room geometry extraction requires Full Access.\n\nContinue?');
      if (!proceed) return;
    }

    // Store the conversion type and transition to version step within the same modal
    setAccConversionType(conversionType === 'ifc' ? 'ifc' : 'rvt');
    setAccPendingLinkFile(file);
    setAccSelectedFileForLink(null); // Close the conversion selector

    // Load versions for the file
    try {
      setAccVersionsLoading(true);
      const versions = await accService.getFileVersions(
        accSelectedConnection.id,
        accSelectedProject.id,
        file.id
      );
      setAccFileVersions(versions);
      // Default to "Always Latest" (null)
      setAccSelectedVersion(null);
      setAccSelectedVersionUrn(null);
      setAccEnableMasterViews(true);
    } catch (error) {
      console.error('Failed to load file versions:', error);
      // If loading versions fails, fall back to immediate linking with latest version
      showErrorToast('Could not load file versions. Linking with latest version.');
      await performACCLinkFile(file, conversionType, null, null, true);
    } finally {
      setAccVersionsLoading(false);
    }
  };

  // Perform the actual file linking after version selection
  const performACCLinkFile = async (
    file: ACCFolderItem,
    conversionType: 'ifc' | 'svf2',
    selectedVersionNumber: number | null,
    selectedVersionUrn: string | null,
    enableMasterViews: boolean
  ) => {
    if (!selectedAsset || !accSelectedConnection || !accSelectedProject || !file.id) return;

    try {
      setAccLinking(true);
      const result = await accService.linkFile(
        selectedAsset.id,
        accSelectedConnection.id,
        accSelectedProject.id,
        accSelectedProject.name,
        accFolderPath.length > 0 ? accFolderPath[accFolderPath.length - 1].id : null,
        file.id,
        file.name,
        conversionType,
        selectedVersionNumber,
        selectedVersionUrn,
        enableMasterViews
      );

      if (result.success) {
        const versionText = selectedVersionNumber ? `v${selectedVersionNumber}` : 'latest version';
        const actionText = conversionType === 'ifc' ? 'Converting to IFC' : 'Preparing for Autodesk Viewer';
        showErrorToast(`Linked ${file.name} (${versionText}). ${actionText} in background.`);
        setShowACCBrowserModal(false);
        setShowAccVersionModal(false);
        // Reload ACC links to show pending sync
        loadACCLinks(selectedAsset.id);
        // Reload asset to show updated status
        loadAssets();
      }
    } catch (error) {
      console.error('Failed to link file:', error);
      showErrorToast('Failed to link file');
    } finally {
      setAccLinking(false);
      setAccPendingLinkFile(null);
    }
  };

  // Handle version selection and link
  const handleVersionSelectAndLink = () => {
    if (!accPendingLinkFile) return;
    const conversionType = accConversionType === 'ifc' ? 'ifc' : 'svf2';
    performACCLinkFile(
      accPendingLinkFile,
      conversionType,
      accSelectedVersion,
      accSelectedVersionUrn,
      accEnableMasterViews
    );
  };

  // Handle version change for existing link + trigger re-sync
  const handleChangeVersionSave = async () => {
    if (!accChangeVersionLink || !selectedAsset) return;
    try {
      setAccLinking(true);
      await accService.updateLinkVersion(accChangeVersionLink.id, accSelectedVersion, accSelectedVersionUrn);
      // Trigger re-sync with new version
      await accService.triggerSync(accChangeVersionLink.id);
      setShowAccVersionModal(false);
      setAccChangeVersionLink(null);
      loadACCLinks(selectedAsset.id);
      // Notify parent (App.tsx) to refresh its ACC links state for the viewer
      onAccLinksChanged?.();
    } catch (err: any) {
      showErrorToast(`Failed to change version: ${err.message || err}`);
    } finally {
      setAccLinking(false);
    }
  };

  // Open version modal for an existing ACC link
  const handleChangeVersion = async (link: ACCLink) => {
    setAccChangeVersionLink(link);
    setAccPendingLinkFile(null); // Not a new link
    // Pre-select current version
    if (link.use_latest_version !== false) {
      setAccSelectedVersion(null);
      setAccSelectedVersionUrn(null);
    } else {
      setAccSelectedVersion(link.selected_version_number || null);
      setAccSelectedVersionUrn(link.selected_version_urn || null);
    }
    setShowAccVersionModal(true);
    // Load versions
    try {
      setAccVersionsLoading(true);
      const versions = await accService.getFileVersions(
        link.acc_connection_id,
        link.acc_project_id,
        link.acc_file_urn // This is the item ID stored during link creation
      );
      setAccFileVersions(versions);
    } catch (error) {
      console.error('Failed to load file versions:', error);
      showErrorToast('Could not load file versions.');
      setShowAccVersionModal(false);
      setAccChangeVersionLink(null);
    } finally {
      setAccVersionsLoading(false);
    }
  };

  // Handle "Direct View" button - use ACC derivatives directly without syncing
  const handleUseDirectView = async (linkId: string) => {
    if (!selectedAsset) return;
    try {
      const result = await accService.useDirectDerivatives(linkId);
      if (result.success) {
        // Reload ACC links to show updated status
        loadACCLinks(selectedAsset.id);
        // Notify parent to refresh viewer state
        onAccLinksChanged?.();
      } else {
        // Show error with suggestion
        showErrorToast(`${result.error}${result.suggestion ? `\n\n${result.suggestion}` : ''}`);
      }
    } catch (error: any) {
      console.error('Direct view failed:', error);
      showErrorToast(`Direct view failed: ${error.message || error}`);
    }
  };

  // Handle "View Direct" from ACC browser - opens version selection first
  const handleACCViewDirect = async (file: ACCFolderItem) => {
    if (!accSelectedConnection || !accSelectedProject || !selectedAsset) {
      showErrorToast('No connection, project, or asset selected');
      return;
    }

    // Set view direct mode and open version selection modal
    setAccViewDirectMode(true);
    setAccPendingLinkFile(file);
    setAccSelectedFileForLink(null);

    // Load versions for the file
    try {
      setAccVersionsLoading(true);
      const versions = await accService.getFileVersions(
        accSelectedConnection.id,
        accSelectedProject.id,
        file.id
      );
      setAccFileVersions(versions);
      // Default to latest version for direct view
      // Set default version (latest) for View Direct
      if (versions.length > 0) {
        setAccSelectedVersion(versions[0].versionNumber);
        setAccSelectedVersionUrn(versions[0].versionUrn);  // versionUrn has ?version=N for version-specific derivatives
      }
    } catch (error) {
      console.error('Failed to load file versions:', error);
      showErrorToast('Could not load file versions.');
      setAccViewDirectMode(false);
      setAccPendingLinkFile(null);
    } finally {
      setAccVersionsLoading(false);
    }
  };

  // Perform the actual direct view after version selection
  const performACCViewDirect = async (
    file: ACCFolderItem,
    selectedVersionNumber: number,
    selectedVersionUrn: string
  ) => {
    if (!accSelectedConnection || !accSelectedProject || !selectedAsset) return;

    try {
      setAccLinking(true);
      const folderId = accFolderPath.length > 0 ? accFolderPath[accFolderPath.length - 1].id : null;

      const result = await accService.viewFileDirect(
        accSelectedConnection.id,
        accSelectedProject.id,
        accSelectedProject.name,
        file.id,
        file.name,
        selectedAsset.id,
        folderId,
        selectedVersionUrn  // Pass the selected version URN
      );

      if (result.success && result.urn && result.viewables && result.connectionId && result.assetId) {
        // Close modals
        setShowACCBrowserModal(false);
        setAccPendingLinkFile(null);
        setAccViewDirectMode(false);

        // Refresh ACC links to show the new link
        loadACCLinks(selectedAsset.id);
        onAccLinksChanged?.();

        // Open the Autodesk Viewer with the direct URN
        onOpenAutodeskViewer?.({
          urn: result.urn,
          connectionId: result.connectionId,
          assetId: result.assetId,
          viewables: result.viewables.map(v => ({
            guid: v.guid,
            name: v.name,
            role: v.role
          }))
        });
      } else {
        // Show error with suggestion
        showErrorToast(`${result.error || 'Failed to get derivatives'}${result.suggestion ? `\n\n${result.suggestion}` : ''}`);
      }
    } catch (error: any) {
      console.error('View direct failed:', error);
      showErrorToast(`View direct failed: ${error.message || error}`);
    } finally {
      setAccLinking(false);
    }
  };

  // Handle version selection confirm for View Direct mode
  const handleVersionSelectAndViewDirect = () => {
    if (!accPendingLinkFile || accSelectedVersion === null || !accSelectedVersionUrn) return;
    performACCViewDirect(accPendingLinkFile, accSelectedVersion, accSelectedVersionUrn);
  };

  const handleACCAddNewConnection = () => {
    // Show scope selection modal instead of direct OAuth
    setShowAccScopeModal(true);
  };

  const loadACCLinks = async (assetId: string) => {
    try {
      const links = await accService.getLinks(assetId);
      setAccLinks(links);
      // Update the set of assets with ACC links
      setAssetsWithAccLinks(prev => {
        const newSet = new Set(prev);
        if (links.length > 0) {
          newSet.add(assetId);
        } else {
          newSet.delete(assetId);
        }
        return newSet;
      });

      // Auto-select "3D ALL" view as default for links without a selected viewable
      let needsRefresh = false;
      for (const link of links) {
        const isReady = accService.isViewerReady(link);
        const hasSelection = !!link.selected_viewable_guid;
        logger.debug(`[SitesPage] Link ${link.acc_file_name}: ready=${isReady}, hasSelection=${hasSelection}, current="${link.selected_viewable_guid}"`);

        if (!hasSelection && isReady) {
          const viewables = accService.getViewables(link);
          const views3d = viewables.filter(v => v.role === '3d');
          logger.debug(`[SitesPage] Available 3D views for ${link.acc_file_name}:`, views3d.map(v => `"${v.name}"`).join(', '));

          // Priority 1: Exact "3D ALL" match (case insensitive)
          let defaultView = views3d.find(v => v.name.toUpperCase() === '3D ALL');

          // Priority 2: Contains "ALL" (e.g., "{3D - All}", "3D ALL PHASES")
          if (!defaultView) {
            defaultView = views3d.find(v => v.name.toUpperCase().includes('ALL'));
          }

          // Priority 3: Default {3D} view
          if (!defaultView) {
            defaultView = views3d.find(v => v.name === '{3D}' || v.name === '3D');
          }

          // Priority 4: First available 3D view
          if (!defaultView && views3d.length > 0) {
            defaultView = views3d[0];
          }

          if (defaultView) {
            logger.debug(`[SitesPage] Auto-selecting 3D view for link ${link.id}: "${defaultView.name}" (guid: ${defaultView.guid})`);
            try {
              await accService.updateLink(link.id, { selected_viewable_guid: defaultView.guid });
              needsRefresh = true;
            } catch (err) {
              console.error('Failed to auto-set 3D view:', err);
            }
          }
        }
      }

      // Refresh links if we made any auto-selections to update the UI
      if (needsRefresh) {
        const refreshedLinks = await accService.getLinks(assetId);
        setAccLinks(refreshedLinks);
      }
    } catch (error) {
      console.error('Failed to load ACC links:', error);
    }
  };

  return {
    showACCBrowserModal, setShowACCBrowserModal,
    accConnections, setAccConnections,
    accSelectedConnection, setAccSelectedConnection,
    accHubs, setAccHubs,
    accSelectedHub, setAccSelectedHub,
    accProjects, setAccProjects,
    accSelectedProject, setAccSelectedProject,
    accFolderContents, setAccFolderContents,
    accFolderPath, setAccFolderPath,
    accLoading, setAccLoading,
    accLinks, setAccLinks,
    accLinking, setAccLinking,
    accSelectedFileForLink, setAccSelectedFileForLink,
    accConversionType, setAccConversionType,
    accDisconnecting, setAccDisconnecting,
    assetsWithAccLinks, setAssetsWithAccLinks,
    showAccScopeModal, setShowAccScopeModal,
    accSelectedScope, setAccSelectedScope,
    showAccVersionModal, setShowAccVersionModal,
    accFileVersions, setAccFileVersions,
    accSelectedVersion, setAccSelectedVersion,
    accSelectedVersionUrn, setAccSelectedVersionUrn,
    accVersionsLoading, setAccVersionsLoading,
    accEnableMasterViews, setAccEnableMasterViews,
    accPendingLinkFile, setAccPendingLinkFile,
    accChangeVersionLink, setAccChangeVersionLink,
    accViewDirectMode, setAccViewDirectMode,
    // handlers
    handleConnectACC,
    handleAccScopeConfirm,
    handleDisconnectACC,
    handleACCSelectConnection,
    handleACCSelectHub,
    handleACCSelectProject,
    handleACCNavigateFolder,
    handleACCNavigateUp,
    handleACCLinkFile,
    handleVersionSelectAndLink,
    handleChangeVersionSave,
    handleChangeVersion,
    handleUseDirectView,
    handleACCViewDirect,
    handleVersionSelectAndViewDirect,
    handleACCAddNewConnection,
    loadACCLinks,
  };
}
